import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import moment from "moment"
import { getLoginLogs, getLoginLogsSummary } from "helpers/fakebackend_helper"

const PER_PAGE = 50

// Blank strings would otherwise be sent as empty query parameters, and the API treats
// a present-but-empty filter differently from an absent one.
const compact = obj =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null))

const LoginActivityTable = ({ token }) => {
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    date_from: "",
    date_to: "",
  })
  // Only applied filters trigger a fetch, so typing in the search box does not fire a
  // request per keystroke.
  const [applied, setApplied] = useState(filters)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getLoginLogs(token, compact({ ...applied, page, per_page: PER_PAGE }))
      setRows(res?.data?.items || [])
      setPagination(res?.data?.pagination || { current_page: 1, last_page: 1, total: 0 })
    } catch (e) {
      setError(
        e?.status === 401
          ? "You need an administrator account to see login activity."
          : e?.message || "Could not load login activity."
      )
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [token, applied, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let cancelled = false
    getLoginLogsSummary(token, 30)
      .then(res => {
        if (!cancelled) setSummary(res?.data || null)
      })
      .catch(() => {
        // The table is the point of the page; a missing summary must not break it.
        if (!cancelled) setSummary(null)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const applyFilters = () => {
    setPage(1)
    setApplied(filters)
  }

  const resetFilters = () => {
    const cleared = { search: "", status: "", date_from: "", date_to: "" }
    setFilters(cleared)
    setPage(1)
    setApplied(cleared)
  }

  const tiles = useMemo(() => {
    if (!summary) return []
    return [
      { label: "Attempts (30 days)", value: summary.total_attempts },
      { label: "Successful", value: summary.successful_logins },
      { label: "Failed", value: summary.failed_attempts },
      { label: "People", value: summary.distinct_users },
      { label: "Addresses", value: summary.distinct_ips },
    ]
  }, [summary])

  return (
    <div className="w-100">
      {tiles.length > 0 && (
        <Row className="mb-3">
          {tiles.map(t => (
            <Col key={t.label} xs="6" md="4" lg="2" className="mb-2">
              <Card className="h-100">
                <CardBody className="py-2 px-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>{t.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 600 }}>{t.value}</div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card className="mb-3">
        <CardBody className="py-2 px-3">
          <Row className="g-2 align-items-end">
            <Col md="4">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>
                Email, address, browser or platform
              </label>
              <Input
                bsSize="sm"
                value={filters.search}
                placeholder="Search"
                onChange={e => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={e => e.key === "Enter" && applyFilters()}
              />
            </Col>
            <Col md="2">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Result</label>
              <Input
                bsSize="sm"
                type="select"
                value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All</option>
                <option value="success">Successful</option>
                <option value="failed">Failed</option>
              </Input>
            </Col>
            <Col md="2">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>From</label>
              <Input
                bsSize="sm"
                type="date"
                value={filters.date_from}
                onChange={e => setFilters({ ...filters, date_from: e.target.value })}
              />
            </Col>
            <Col md="2">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>To</label>
              <Input
                bsSize="sm"
                type="date"
                value={filters.date_to}
                onChange={e => setFilters({ ...filters, date_to: e.target.value })}
              />
            </Col>
            <Col md="2" className="d-flex gap-2">
              <Button size="sm" color="primary" onClick={applyFilters}>Apply</Button>
              <Button size="sm" color="secondary" outline onClick={resetFilters}>Reset</Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error && <div className="alert alert-warning py-2">{error}</div>}

      <Card>
        <CardBody className="p-0">
          <div className="table-responsive">
            <Table className="table-nowrap mb-0" hover>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Result</th>
                  <th>From (IP)</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Spinner size="sm" /> <span className="ms-2">Loading…</span>
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && !error && (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No login activity matches these filters.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map(r => (
                    <tr key={r.id}>
                      <td title={r.created_at}>
                        {moment(r.created_at).format("DD MMM YYYY, HH:mm:ss")}
                      </td>
                      <td>{r.user?.full_name || <span className="text-muted">unknown account</span>}</td>
                      <td>{r.email}</td>
                      <td>{r.user?.role || "—"}</td>
                      <td>
                        {r.status === "success" ? (
                          <Badge color="success">success</Badge>
                        ) : (
                          <Badge color="danger" title={r.failure_reason || ""}>
                            {r.failure_reason === "unknown_email" ? "no such account" : "wrong password"}
                          </Badge>
                        )}
                      </td>
                      <td><code>{r.ip_address || "—"}</code></td>
                      <td title={r.user_agent}>
                        {[r.browser, r.platform, r.device_type].filter(Boolean).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <div className="text-muted" style={{ fontSize: 12 }}>
          {pagination.total} record{pagination.total === 1 ? "" : "s"} · page{" "}
          {pagination.current_page} of {pagination.last_page}
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            outline
            disabled={loading || pagination.current_page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            outline
            disabled={loading || pagination.current_page >= pagination.last_page}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

LoginActivityTable.propTypes = {
  token: PropTypes.string,
}

export default LoginActivityTable
